import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { validateEmail, validatePassword, validatePasswordMatch } from '../../utils/validation';

function Login() {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [showConfirmCode, setShowConfirmCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);

  const { login, forgotPassword, confirmPassword, completeNewPassword, setTranslationFunction } = useAuth();
  
  // Configura a função de tradução no AuthContext
  React.useEffect(() => {
    if (setTranslationFunction) {
      setTranslationFunction(t);
    }
  }, [t, setTranslationFunction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const result = await login(credentials.email, credentials.password);
      if (!result.success) {
        if (result.needsNewPassword) {
          setNeedsNewPassword(true);
          setCognitoUser(result.cognitoUser);
          setError('Você precisa definir uma nova senha.');
        } else {
          setError(result.error || 'Erro ao fazer login');
        }
      }
    } catch (error) {
      console.error('Login catch error:', error);
      let errorMessage = 'Erro ao fazer login';
      
      if (error.response?.status === 400) {
        errorMessage = 'Email ou senha inválidos';
      } else if (error.response?.status === 401) {
        errorMessage = 'Credenciais inválidas';
      } else if (error.response?.status === 403) {
        errorMessage = 'Acesso negado';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setResetMessage('Por favor, insira seu email.');
      return;
    }

    if (!validateEmail(resetEmail)) {
      setResetMessage('Por favor, insira um email válido.');
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      const result = await forgotPassword(resetEmail);
      if (result.success) {
        setResetMessage(result.message);
        setShowConfirmCode(true);
      } else {
        setResetMessage(result.error);
      }
    } catch (error) {
      setResetMessage('Erro ao enviar código de recuperação.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleConfirmPassword = async () => {
    if (!verificationCode || !newPassword || !confirmNewPassword) {
      setResetMessage('Por favor, preencha todos os campos.');
      return;
    }

    if (!validatePasswordMatch(newPassword, confirmNewPassword)) {
      setResetMessage('As senhas não coincidem.');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setResetMessage(`Senha inválida: ${passwordValidation.errors.join(', ')}`);
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      const result = await confirmPassword(resetEmail, verificationCode, newPassword);
      if (result.success) {
        setResetMessage(result.message);
        setTimeout(() => {
          handleCloseForgotPassword();
        }, 2000);
      } else {
        setResetMessage(result.error);
      }
    } catch (error) {
      setResetMessage('Erro ao alterar senha.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setResetEmail('');
    setResetMessage('');
    setShowConfirmCode(false);
    setVerificationCode('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleCompleteNewPassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      setError('Por favor, preencha todos os campos.');
      return;
    }

    if (!validatePasswordMatch(newPassword, confirmNewPassword)) {
      setError('As senhas não coincidem.');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(`Senha inválida: ${passwordValidation.errors.join(', ')}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await completeNewPassword(cognitoUser, newPassword);
      if (!result.success) {
        setError(result.error || 'Erro ao definir nova senha');
      }
    } catch (error) {
      setError('Erro ao definir nova senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img src="/logo.png" alt="TrackHealth Logo" style={{ maxHeight: '60px', marginBottom: '16px' }} />
          </Box>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            {t('login.title')}
          </Typography>
          <Typography variant="h6" align="center" color="textSecondary" gutterBottom>
            {t('login.subtitle')}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={needsNewPassword ? handleCompleteNewPassword : handleSubmit} sx={{ mt: 1 }}>
            {!needsNewPassword ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t('login.email')}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={credentials.email}
                  onChange={handleChange}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t('login.password')}
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={handleChange}
                />
              </>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Defina uma nova senha para sua conta: {credentials.email}
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Nova Senha"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  helperText="Mínimo 8 caracteres, incluindo maiúscula, minúscula, número e símbolo"
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Confirmar Nova Senha"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                />
              </>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? (needsNewPassword ? 'Definindo...' : t('login.loggingIn')) : (needsNewPassword ? 'Definir Nova Senha' : t('login.login'))}
            </Button>
            
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setForgotPasswordOpen(true)}
                sx={{ cursor: 'pointer' }}
              >
                {t('login.forgotPassword')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
      
      {/* Dialog Esqueci Senha */}
      <Dialog open={forgotPasswordOpen} onClose={handleCloseForgotPassword} maxWidth="sm" fullWidth>
        <DialogTitle>{t('login.resetPassword')}</DialogTitle>
        <DialogContent>
          {!showConfirmCode ? (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {t('login.enterEmail')}
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('login.dedicatedPage')}{' '}
                <Link 
                  component="button" 
                  onClick={() => {
                    window.open(`/reset-password?email=${resetEmail}`, '_blank');
                  }}
                  disabled={!resetEmail}
                >
                  {t('login.here')}
                </Link>
              </Alert>
              
              <TextField
                autoFocus
                margin="dense"
                label={t('login.email')}
                type="email"
                fullWidth
                variant="outlined"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={resetLoading || showConfirmCode}
              />
            </>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {t('login.enterEmail')} {resetEmail}.
              </Typography>
              
              <TextField
                autoFocus
                margin="dense"
                label={t('login.verificationCode')}
                fullWidth
                variant="outlined"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={resetLoading}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                label={t('login.newPassword')}
                type="password"
                fullWidth
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={resetLoading}
                sx={{ mb: 1 }}
                helperText={t('login.passwordRequirements')}
              />
              
              {newPassword && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    {t('login.passwordRequirementsTitle')}
                  </Typography>
                  <List dense>
                    {validatePassword(newPassword).errors.map((error, index) => (
                      <ListItem key={index} sx={{ py: 0, px: 1 }}>
                        <ListItemText 
                          primary={error} 
                          primaryTypographyProps={{ 
                            variant: 'caption', 
                            color: 'error' 
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              <TextField
                margin="dense"
                label={t('login.confirmPassword')}
                type="password"
                fullWidth
                variant="outlined"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                disabled={resetLoading}
              />
            </>
          )}
          
          {resetMessage && (
            <Alert 
              severity={resetMessage.includes('sucesso') || resetMessage.includes('enviado') ? 'success' : 'error'} 
              sx={{ mt: 2 }}
            >
              {resetMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForgotPassword} disabled={resetLoading}>
            {t('common.cancel')}
          </Button>
          {!showConfirmCode ? (
            <Button 
              onClick={handleForgotPassword} 
              variant="contained"
              disabled={resetLoading}
            >
              {resetLoading ? t('login.sending') : t('login.sendCode')}
            </Button>
          ) : (
            <Button 
              onClick={handleConfirmPassword} 
              variant="contained"
              disabled={resetLoading}
            >
              {resetLoading ? t('login.changing') : t('login.changePassword')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Login;